/**
 * Collection review summary for Collection Banner V2 ("★ 4.7 from 2,947 reviews of …").
 *
 * <lp-collection-rating> fetches the collection's JSON view
 * (templates/collection.review-summary.liquid), every page of 50 products in
 * parallel, merges the [product id, review count, average rating] entries and
 * writes the line.
 *
 * - Okendo product groups give every member the group's totals (all 119 grouped
 *   needlepoint pillows report the same 1,272 reviews), so each group is counted
 *   once, not once per product.
 * - Products showing the same total are a group or unrelated products that
 *   happen to match (three ornaments with one 5-star review each). See
 *   countReviewSets for how the two are told apart.
 * - The average is weighted by review count, so a product with one review does
 *   not count as much as one with a thousand.
 * - The result is cached per collection in localStorage for a day, and shown
 *   at once on later visits; after a day it is refreshed in the background.
 * - If nothing comes back, or the total is under the section's minimum, the
 *   element hides itself.
 */

const OKENDO_API_ROOT = 'https://api.okendo.io/v1';
const CACHE_HOURS = 24;
const FETCH_TIMEOUT_MS = 8000;
// Checked against every published product on 2026-10-28: every total of this
// many reviews or more shown on several products is a real Okendo group, so
// those are merged without asking Okendo. Smaller repeated totals include
// coincidences and are checked one product at a time.
const GROUP_MIN_REVIEWS = 10;

/**
 * @typedef {[number, number, number]} ProductReviews - [product id, review count, average rating]
 */

/**
 * @typedef {Object} ReviewSummary
 * @property {number} count - Reviews across the collection, groups counted once
 * @property {number} rating - Average rating weighted by review count
 */

class LpCollectionRating extends HTMLElement {
  abortController = undefined;

  connectedCallback() {
    this.load();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  get textElement() {
    return this.querySelector('[data-rating-text]');
  }

  get cacheKey() {
    return `lp-collection-rating:v2:${this.dataset.collectionUrl}`;
  }

  async load() {
    this.abortController?.abort();
    this.abortController = undefined;

    // A cached line shows straight away, even past its expiry; an expired one is refreshed behind it.
    const cached = this.readCache();
    if (cached) this.render(cached.summary);
    if (cached?.fresh) return;

    const controller = new AbortController();
    this.abortController = controller;
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    let summary = null;

    try {
      const products = await this.fetchProducts(controller.signal);
      summary = await this.summarize(products, controller.signal);
      this.writeCache(summary);
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    } finally {
      clearTimeout(timeout);
    }

    // A newer load() has taken over and renders instead.
    if (controller !== this.abortController) return;

    // A failed refresh keeps the cached line rather than hiding it.
    if (summary || !cached) this.render(summary);
  }

  /**
   * @param {AbortSignal} signal
   * @returns {Promise<ProductReviews[]>}
   */
  async fetchProducts(signal) {
    const pages = Math.max(1, Number(this.dataset.pages) || 1);
    const requests = [];

    for (let page = 1; page <= pages; page++) {
      const url = new URL(this.dataset.collectionUrl, window.location.origin);
      url.searchParams.set('view', 'review-summary');
      url.searchParams.set('page', String(page));
      requests.push(this.fetchPage(url, signal));
    }

    const results = await Promise.all(requests);
    return results.flat();
  }

  /**
   * @param {URL} url
   * @param {AbortSignal} signal
   * @returns {Promise<ProductReviews[]>}
   */
  async fetchPage(url, signal) {
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(`Review summary request failed with status ${response.status}`);

    const data = JSON.parse(await response.text());
    return Array.isArray(data.products) ? data.products : [];
  }

  /**
   * Buckets products by the total they show, then adds each bucket once per
   * separate set of reviews in it.
   * @param {ProductReviews[]} products
   * @param {AbortSignal} signal
   * @returns {Promise<ReviewSummary>}
   */
  async summarize(products, signal) {
    const buckets = new Map();

    for (const [id, reviews, rating] of products) {
      const key = `${reviews}:${rating}`;
      if (!buckets.has(key)) buckets.set(key, { reviews, rating, ids: [] });
      buckets.get(key).ids.push(id);
    }

    // Every bucket is checked at once; awaiting them in turn chained one Okendo round trip per bucket.
    const sets = await Promise.all(
      [...buckets.values()].map(async ({ reviews, rating, ids }) => {
        const reviewSets = ids.length > 1 && reviews < GROUP_MIN_REVIEWS ? await this.countReviewSets(ids, signal) : 1;
        return { reviews, rating, reviewSets };
      })
    );

    let count = 0;
    let total = 0;

    for (const { reviews, rating, reviewSets } of sets) {
      count += reviews * reviewSets;
      total += reviews * rating * reviewSets;
    }

    return { count, rating: count ? total / count : 0 };
  }

  /**
   * Products in one Okendo group list the same newest review; unrelated
   * products each list their own. The number of distinct newest reviews is the
   * number of separate review sets among these products.
   * @param {number[]} ids - Products showing the same small total
   * @param {AbortSignal} signal
   * @returns {Promise<number>}
   */
  async countReviewSets(ids, signal) {
    const userId = this.dataset.okendoUserId;
    if (!userId) return ids.length;

    const newestReviews = await Promise.all(ids.map((id) => this.fetchNewestReviewId(userId, id, signal)));
    // A product Okendo did not answer for is kept as its own set.
    return new Set(newestReviews.map((reviewId, index) => reviewId || `product:${ids[index]}`)).size;
  }

  /**
   * @param {string} userId
   * @param {number} productId
   * @param {AbortSignal} signal
   * @returns {Promise<string>}
   */
  async fetchNewestReviewId(userId, productId, signal) {
    const url = new URL(`${OKENDO_API_ROOT}/stores/${encodeURIComponent(userId)}/products/shopify-${productId}/reviews`);
    url.searchParams.set('limit', '1');
    url.searchParams.set('orderBy', 'date desc');

    try {
      const response = await fetch(url, { signal });
      if (!response.ok) return '';

      const data = await response.json();
      return data.reviews?.[0]?.reviewId ?? '';
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      return '';
    }
  }

  /**
   * @param {ReviewSummary|null} summary
   */
  render(summary) {
    const minReviews = Number(this.dataset.minReviews) || 0;
    if (!summary || !summary.count || summary.count < minReviews || !this.textElement) {
      this.hidden = true;
      return;
    }

    const locale = document.documentElement.lang || undefined;
    const rating = summary.rating.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    const count = summary.count.toLocaleString(locale);

    // The average is set in <strong> so it reads bold; the rest stays plain text.
    const parts = this.dataset.text.replaceAll('[count]', count).split('[rating]');
    const nodes = [];

    for (const [index, part] of parts.entries()) {
      if (index > 0) {
        const value = document.createElement('strong');
        value.textContent = rating;
        nodes.push(value);
      }

      nodes.push(part);
    }

    this.textElement.replaceChildren(...nodes);
    this.hidden = false;
    this.dataset.state = 'ready';
  }

  /**
   * @returns {{summary: ReviewSummary, fresh: boolean}|null}
   */
  readCache() {
    try {
      const cached = JSON.parse(localStorage.getItem(this.cacheKey));
      if (cached?.summary) return { summary: cached.summary, fresh: Date.now() - cached.time < CACHE_HOURS * 3600000 };
    } catch (error) {
      // Storage can be blocked or hold bad data; fetching again is fine.
    }
    return null;
  }

  /**
   * @param {ReviewSummary} summary
   */
  writeCache(summary) {
    try {
      localStorage.setItem(this.cacheKey, JSON.stringify({ time: Date.now(), summary }));
    } catch (error) {
      // Storage can be full or blocked; the line still renders without it.
    }
  }
}

if (!customElements.get('lp-collection-rating')) {
  customElements.define('lp-collection-rating', LpCollectionRating);
}
